import React from 'react';
import TopNavigation from '../components/TopNavigation';
import { useTheme } from '@/contexts/ThemeContext';

interface AboutPageProps {
  onNavigateToExplore?: () => void;
  onNavigateToLibrary?: () => void;
  onNavigateToProfile?: () => void;
  onNavigateToMyBooks?: () => void;
  onNavigateHome?: () => void;
  onNavigateToLogin?: (source?: string) => void;
  onNavigateToSignup?: (source?: string) => void;
  onToggleMenu: () => void;
  onBookSelect: (bookSlug: string) => void;
  isAuthenticated: boolean;
}

const AboutPage: React.FC<AboutPageProps> = ({ onNavigateToExplore, onNavigateToLibrary, onNavigateToProfile, onNavigateToMyBooks, onNavigateHome, onNavigateToLogin, onNavigateToSignup, onToggleMenu, isAuthenticated }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  const themeClasses = isDarkMode ? 'text-theme-primary-dark' : 'bg-theme-light text-theme-primary-light';

  return (
    <div className={`w-full pt-20 ${themeClasses} transition-colors duration-300`} style={{ backgroundColor: isDarkMode ? 'var(--background-colour-dark-mode)' : 'var(--background-colour-light-mode)' }}>
      <TopNavigation
        isDarkMode={isDarkMode}
        onToggleMenu={onToggleMenu}
        onNavigateHome={onNavigateHome}
        onNavigateToExplore={onNavigateToExplore}
        onNavigateToLibrary={onNavigateToLibrary}
        onNavigateToLogin={(source) => onNavigateToLogin?.(source)}
        onNavigateToSignup={(source) => onNavigateToSignup?.(source)}
        onNavigateToProfile={onNavigateToProfile}
        onNavigateToMyBooks={onNavigateToMyBooks}
        showProfileIcon={isAuthenticated}
      />
      {/* Main Content - Full Width Responsive */}
      <div className="w-full flex flex-col px-8 lg:px-16 xl:px-24 pb-[calc(10rem+var(--safe-bottom))]">
        {/* Hero Section */}
        <header className="text-left w-full my-24 max-w-5xl mx-auto">
          <h2 className={`portrait-text-app mb-4 text-7xl lg:text-8xl xl:text-9xl font-bold leading-tight line-height-.2 -mt-1 ${
            isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'
          }`}>
            About
          </h2>
          <p className={`text-xl ${
              isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'
            }`}>Reading is a solitary art – but books really come alive in conversation. We all know the thrill of rediscovering a great book through conversations with a friend, and the moment when someone else's insight suddenly illuminates a world of ideas.</p>
          {/* Colored Element */}
          <div className="flex justify-end my-8">
            <div className={`w-48 h-5 ${isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'}`}></div>
          </div>
        </header>

        {/* About Content Section */}
        <div className={`w-full max-w-5xl mx-auto py-36 border-t ${
          isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'
        }`}>
          <div className={`space-y-6 ${isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'}`}>
            <p className="text-lg leading-relaxed">
              But conversations can be limited by geography, time, and the fear of asking an obvious question. Imagine if you could carry a guide in your pocket who can answer any question you might have – without judgment, and with a sense of mutual exploration. <em>What does the word "scrivener" mean? Why can't Cathy marry Heathcliff? Why is the wife in the attic?</em>
            </p>

            <p className="text-lg leading-relaxed">
              Founded by the team behind <a 
                href="https://tank.tv/" 
                target="_blank" 
                rel="noopener noreferrer"
                className={`underline underline-offset-4 ${isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'}`}
              >
                TANK Magazine
              </a>, long known for its expertise in cultural curation and intellectual curiosity, Aleph delivers the expertise and the freedom to make your own way through the canon.
            </p>

            <p className="text-lg leading-relaxed">
              Aleph is created in collaboration with leading technologists with a deep understanding of AI and its applications. We're bringing together literary editors, critics, and researchers to create a new reading experience: where human imagination meets AI's infinite patience.
            </p>

            <p className="text-lg leading-relaxed font-regular">
            It’s simple: great books deserve great conversations.
            </p>
          </div>
        </div>
        
        {/* Team Spotlight Section */}
        <div className={`w-full max-w-5xl mx-auto py-36 border-t ${
          isDarkMode ? 'border-white' : 'border-blue-700'
        }`}>
          <div className={`space-y-8 ${isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'}`}>
            <h2 className={`portrait-text-app text-2xl lg:text-3xl font-bold mb-4 ${
              isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'
            }`}>
              TEAM SPOTLIGHT
            </h2>
            
            <div className="space-y-8">
              <div>
                <p className="text-lg leading-relaxed">
                  <span>
                    <a 
                      href="https://arkitus.com/research/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={`underline underline-offset-4 ${isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'}`}
                    >
                      Ali Eslami
                    </a>
                  </span>
                  , Investor and Lead Advisor<br />
                  Director and Distinguished Research Scientist at Google DeepMind
                </p>
              </div>

              <div>
                <p className="text-lg leading-relaxed">
                  <span>
                    <a 
                      href="https://jamez.it/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={`underline underline-offset-4 ${isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'}`}
                    >
                      Giacomo Miceli
                    </a>
                  </span>
                  , Co-founder and CTO<br />
                  Full Stack/Creative LLM and GenAI expert, known for his <br/> creative use of technology such as The Infinite Conversation
                </p>
              </div>

              <div>
                <p className="text-lg leading-relaxed">
                  <span>
                    <a 
                      href="https://tank.tv/magazine/issue-103/features/barbara-eplers-best-books" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={`underline underline-offset-4 ${isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'}`}
                    >
                      Barbara Epler
                    </a>
                  </span>
                  , Advisor and Literary Editor<br />
                  Editor in Chief and Publishing Director New Directions NYC
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* FAQ Section */}
        <div className={`w-full max-w-5xl mx-auto pt-36 pb-18 border-t ${
          isDarkMode ? 'border-white' : 'border-blue-700'
        }`}>
          <div className={`space-y-6 ${isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'}`}>
            <h2 className={`portrait-text-app text-2xl lg:text-3xl font-bold mb-4 ${
              isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'
            }`}>
              FAQ
            </h2>
            
            <div className="space-y-8">
              <div>
                <h3 className={`portrait-text-app text-xl font-bold mb-2 ${isDarkMode ? 'text-theme-primary-dark' : 'text-blue-700'}`}>
                  What can Aleph do?
                </h3>
                <p className="text-lg leading-relaxed">
                Aleph transforms classic books into interactive conversations. Unlike traditional audiobooks, it offers voice control, semantic search, and AI-powered discussions. Ask questions about word meanings, literary themes, historical context, or explore deeper connections between texts—all while avoiding spoilers.
                </p>
              </div>

              <div>
                <h3 className={`portrait-text-app text-xl font-bold mb-2 ${isDarkMode ? 'text-theme-primary-dark' : 'text-blue-700'}`}>
                  What can I expect from Aleph Beta?
                </h3>
                <p className="text-lg leading-relaxed">
                Aleph Beta is our testbed to refine the product and experience, your use and feedback help us improve. During the beta period you’ll get 30 minutes of free conversation with Aleph and unlimited audiobook listening across all books available in our library. To check your usage, simply head over to your profile and check ‘Usage History’.
                </p>
              </div>

              <div>
                <h3 className={`portrait-text-app text-xl font-bold mb-2 ${isDarkMode ? 'text-theme-primary-dark' : 'text-blue-700'}`}>
                  Who is this app for?
                </h3>
                <p className="text-lg leading-relaxed">
                Anyone who wants to explore great literature, whether for pleasure, school, or work. Aleph makes intimidating classics accessible by turning reading into a conversation. It's designed to enhance—not replace—your engagement with literary masterpieces, inviting you to dive deeper rather than skim the surface.
                </p>
              </div>

              <div>
                <h3 className={`portrait-text-app text-xl font-bold mb-2 ${isDarkMode ? 'text-theme-primary-dark' : 'text-blue-700'}`}>
                  What makes Aleph special?
                </h3>
                <p className="text-lg leading-relaxed">
                Aleph combines technical innovation with expertly curated content, creating a personalized literary tutor. It reads along with you without spoiling plots, adapts to your knowledge level (from high school to PhD), and uses each book as a springboard for broader discussions. Think of it as having a world-class literature professor who's also a friendly conversation partner.
                </p>
              </div>

              <div>
                <h3 className={`portrait-text-app text-xl font-bold mb-2 ${isDarkMode ? 'text-theme-primary-dark' : 'text-blue-700'}`}>
                  How is this different from a regular AI chatbot?
                </h3>
                <p className="text-lg leading-relaxed">
                Regular chatbots can't read books aloud or track your reading progress. They provide generic responses, often include spoilers, and aren't optimized for long literary discussions. Aleph is specifically designed for extended book conversations, maintaining context throughout your reading journey while adapting to your evolving understanding.
                </p>
              </div>
              <div>
                <h3 className={`portrait-text-app text-xl font-bold mb-2 ${isDarkMode ? 'text-theme-primary-dark' : 'text-blue-700'}`}>
                What's it like to chat with Aleph?
                </h3>
                <p className="text-lg leading-relaxed">
                Imagine discussing a book with an enthusiastic, knowledgeable friend who never judges you or your questions. Aleph adapts its language and depth to match your needs—whether you want casual observations or scholarly analysis. Conversations feel natural and engaging, not robotic or academic.
                </p>
              </div>
              <div>
                <h3 className={`portrait-text-app text-xl font-bold mb-2 ${isDarkMode ? 'text-theme-primary-dark' : 'text-blue-700'}`}>
                Which books are available in Beta?
                </h3>
                <p className="text-lg leading-relaxed">
                Aleph launches with a carefully selected collection of 24 classic literature, chosen for their richness and educational value. We continuously expand our library based on user requests and literary significance. All books are in the public domain or properly licensed.
                </p>
              </div>

              <div>
                <h3 className={`portrait-text-app text-xl font-bold mb-2 ${isDarkMode ? 'text-theme-primary-dark' : 'text-blue-700'}`}>
                Can Aleph help with academic work?
                </h3>
                <p className="text-lg leading-relaxed">
                Absolutely. Aleph can assist with essay preparation, discussion questions, and literary analysis. However, it's designed to enhance your understanding rather than do your homework for you. Think of it as a study partner, not a shortcut. 
                </p>
              </div>
            </div>

            <p className="text-lg leading-relaxed mt-6">
              For more information on Aleph, feel free to reach out to us on{' '}
              <a 
                href="mailto:say@hi-aleph.com"
                className={`underline underline-offset-4 ${isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'}`}
              >
                say@hi-aleph.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
